/** Webenv-specific state management tools (5 tools). */

import type { TevmClients } from "../context.js";
import type { ToolResult } from "../types.js";

export interface StateContext {
  clients: TevmClients;
  saveSnapshot(name: string, description?: string): Promise<void>;
  restoreSnapshot(name: string): Promise<void>;
  listSnapshots(): Promise<
    { name: string; description?: string; timestamp: number }[]
  >;
  exportState(): Promise<unknown>;
  importState(state: unknown): Promise<void>;
}

function toToolResult(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data, null, 2) }] };
}

function toErrorResult(message: string): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify({ error: message }) }],
    isError: true,
  };
}

export async function takeSnapshotHandler(
  params: Record<string, unknown>,
  ctx: StateContext,
): Promise<ToolResult> {
  const name = params.name as string;
  const description = params.description as string | undefined;
  if (!name) {
    return toErrorResult("name is required");
  }
  try {
    await ctx.saveSnapshot(name, description);
    return toToolResult({
      name,
      description,
      status: "saved",
      timestamp: Date.now(),
    });
  } catch (err) {
    return toErrorResult(
      `Snapshot failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function restoreSnapshotHandler(
  params: Record<string, unknown>,
  ctx: StateContext,
): Promise<ToolResult> {
  const name = params.name as string;
  if (!name) {
    return toErrorResult("name is required");
  }
  try {
    await ctx.restoreSnapshot(name);
    return toToolResult({ name, status: "restored" });
  } catch (err) {
    return toErrorResult(
      `Restore failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function listSnapshotsHandler(
  _params: Record<string, unknown>,
  ctx: StateContext,
): Promise<ToolResult> {
  try {
    const snapshots = await ctx.listSnapshots();
    return toToolResult({ count: snapshots.length, snapshots });
  } catch (err) {
    return toErrorResult(
      `List failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function exportStateHandler(
  _params: Record<string, unknown>,
  ctx: StateContext,
): Promise<ToolResult> {
  try {
    const state = await ctx.exportState();
    return toToolResult({ status: "exported", state });
  } catch (err) {
    return toErrorResult(
      `Export failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function importStateHandler(
  params: Record<string, unknown>,
  ctx: StateContext,
): Promise<ToolResult> {
  const state = params.state;
  if (!state) {
    return toErrorResult("state is required");
  }
  try {
    await ctx.importState(state);
    return toToolResult({
      status: "imported",
      message: "State loaded successfully",
    });
  } catch (err) {
    return toErrorResult(
      `Import failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** All state management tool definitions for registration. */
export const STATE_TOOLS = [
  {
    name: "take_snapshot",
    description:
      "Save a named snapshot of the current chain state to IndexedDB",
    handler: takeSnapshotHandler,
  },
  {
    name: "restore_snapshot",
    description: "Restore chain state from a named snapshot",
    handler: restoreSnapshotHandler,
  },
  {
    name: "list_snapshots",
    description:
      "List all saved snapshots with names, descriptions, and timestamps",
    handler: listSnapshotsHandler,
  },
  {
    name: "export_state",
    description: "Export the full chain state as JSON for backup or sharing",
    handler: exportStateHandler,
  },
  {
    name: "import_state",
    description: "Import chain state from a JSON export",
    handler: importStateHandler,
  },
] as const;
